import { useRef, useState, useEffect, useCallback } from 'react'

/**
 * Tracks whether a scroll gesture started inside or outside a scrollable container.
 *
 * When `panOnScroll` is enabled in React Flow, wheel events pan the canvas.
 * React Flow checks every wheel event for the `nowheel` CSS class — if the event
 * target is inside a `.nowheel` element, React Flow ignores it. This is a per-event
 * check, not per-gesture, so a canvas pan that merely passes over a scrollable
 * component gets abruptly stolen.
 *
 * This hook solves that by dynamically toggling `shouldCaptureWheel`:
 * - If the mouse was already inside the container when the scroll gesture started → true
 * - If the gesture started outside (canvas pan) and the cursor moved over → false
 *
 * The consumer conditionally applies `nowheel` based on `shouldCaptureWheel`.
 */

const GESTURE_IDLE_TIMEOUT_MS = 150

type GestureState = 'idle' | 'panning' | 'scrolling'

interface UseScrollIntentResult {
  containerRef: React.RefObject<HTMLDivElement | null>
  shouldCaptureWheel: boolean
}

export function useScrollIntent(): UseScrollIntentResult {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isMouseInsideRef = useRef(false)
  const gestureStateRef = useRef<GestureState>('idle')
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [shouldCaptureWheel, setShouldCaptureWheel] = useState(false)

  // Track mouse enter/leave on the container
  const handleMouseEnter = useCallback(() => {
    isMouseInsideRef.current = true
  }, [])

  const handleMouseLeave = useCallback(() => {
    isMouseInsideRef.current = false
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mouseenter', handleMouseEnter)
    container.addEventListener('mouseleave', handleMouseLeave)

    // Detect scroll gesture start/continuation via wheel events on document (capture phase).
    // This fires before React Flow's handler, so we can decide whether to show `nowheel`.
    const handleWheel = (e: WheelEvent): void => {
      // Reset the idle timer on every wheel event
      if (idleTimerRef.current !== null) {
        clearTimeout(idleTimerRef.current)
      }

      // Sync mouse-inside state from the actual event target. This is more
      // reliable than mouseenter/mouseleave alone because those events do NOT
      // fire when the element moves under a stationary cursor (e.g. canvas
      // panning repositions a node beneath the pointer).
      isMouseInsideRef.current = containerRef.current?.contains(e.target as Node) ?? false

      // On first wheel event of a gesture, decide based on cursor position
      if (gestureStateRef.current === 'idle') {
        if (isMouseInsideRef.current) {
          gestureStateRef.current = 'scrolling'
          setShouldCaptureWheel(true)
        } else {
          gestureStateRef.current = 'panning'
          setShouldCaptureWheel(false)
        }
      }

      // After 150ms of no wheel events, reset to idle
      idleTimerRef.current = setTimeout(() => {
        gestureStateRef.current = 'idle'
        idleTimerRef.current = null
        // Reset to default: capture wheel when idle (mouse hover = intent to interact)
        setShouldCaptureWheel(isMouseInsideRef.current)
      }, GESTURE_IDLE_TIMEOUT_MS)
    }

    // Passive listener in capture phase — we only read state, never preventDefault
    document.addEventListener('wheel', handleWheel, { capture: true, passive: true })

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter)
      container.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('wheel', handleWheel, { capture: true })
      if (idleTimerRef.current !== null) {
        clearTimeout(idleTimerRef.current)
      }
    }
  }, [handleMouseEnter, handleMouseLeave])

  return { containerRef, shouldCaptureWheel }
}
